import Head from 'next/head';

import { Currency } from '@app/models/api/api-types';
import { accommodationSlice, accommodationStaticSlice, useAppSelector } from '@app/state';

import { OtaDatesJsonLdProps } from './ota-dates-json-ld.props';

import {
    checkIfPetsAllowed,
    createAddress,
    createAggregateRating,
    createAmenityFeatures,
    createGeo,
    createHotelRoomProduct,
    createImageVariants,
    createLodgingReservationOffer,
    SCHEMA_URL,
    trimDescription,
} from '../json-ld.utils';

export const OtaDatesJsonLd = ({ accommodationId, unitId }: OtaDatesJsonLdProps) => {
    const { checkInStart, checkOutStart } = useAppSelector(
        accommodationStaticSlice.selectors.selectImportantInfoData,
    );
    const {
        name,
        description: fullDescription,
        image,
        addressCountry,
        addressLocality,
        addressRegion,
        postalCode,
        streetAddress,
        latitude,
        longitude,
        ratingAverage,
        ratingCount,
        services,
        internalAmenityGroups,
        labels,
        currency,
    } = useAppSelector(accommodationStaticSlice.selectors.selectRichSnippetsData);
    const accommodation = useAppSelector(accommodationSlice.selectors.selectCurrentAccommodation);
    const filters = useAppSelector(accommodationSlice.selectors.selectFilters);

    if (!filters?.checkIn || !filters?.checkOut) {
        return null;
    }

    if (
        !accommodation ||
        !name ||
        !fullDescription ||
        !labels ||
        !internalAmenityGroups ||
        !services ||
        !image ||
        !latitude ||
        !longitude ||
        !ratingAverage ||
        !ratingCount ||
        !services
    ) {
        return null;
    }

    const priceCurrency = accommodation.convertedRatesCurrency ?? currency ?? Currency.Pln;

    const description = trimDescription(fullDescription);
    const petsAllowed = checkIfPetsAllowed({ labels, internalAmenityGroups, services });
    const images = createImageVariants(image);
    const address = createAddress({
        addressCountry,
        addressLocality,
        addressRegion,
        postalCode,
        streetAddress,
    });
    const geo = createGeo({ latitude, longitude });
    const aggregateRating = createAggregateRating({ ratingAverage, ratingCount });
    const amenityFeature = createAmenityFeatures({ services });

    const apartments = accommodation.apartments?.filter(apartment => !!apartment) ?? [];
    const visibleApartments = unitId ? apartments.filter(({ id }) => unitId === id) : apartments;

    const makesOffer = visibleApartments.flatMap(apartment =>
        apartment.variants
            ?.filter(variant => !!variant)
            .map((variant, identifierIndex) =>
                createLodgingReservationOffer({
                    withIdentifier: true,
                    identifierIndex,
                    checkInStartDate: filters.checkIn ?? '',
                    checkOutStartDate: filters.checkOut ?? '',
                    checkInStartTime: checkInStart ?? '',
                    checkOutStartTime: checkOutStart ?? '',
                    priceCurrency,
                    apartment,
                    variant,
                }),
            ),
    );

    const containsPlace = visibleApartments
        .filter(({ variants }) => variants && variants.length > 0)
        .map(apartment =>
            createHotelRoomProduct({
                checkInStartDate: filters.checkIn ?? '',
                checkInStartTime: checkInStart ?? '',
                checkOutStartDate: filters.checkOut ?? '',
                checkOutStartTime: checkOutStart ?? '',
                priceCurrency,
                apartment,
            }),
        );

    const jsonLd = {
        '@context': SCHEMA_URL,
        '@type': 'Hotel',
        name,
        identifier: accommodationId,
        description,
        petsAllowed,
        image: images,
        ...(address && { address }),
        geo,
        aggregateRating,
        amenityFeature,
        makesOffer,
        containsPlace,
    };

    return (
        <Head>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />
        </Head>
    );
};
